/**
 * DataDriven Commonsense - Secure Provider Portal
 * React Frontend with Firebase Auth
 */

import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useNavigate } from 'react-router-dom';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import { useAuthState } from 'react-firebase-hooks/auth';
import UploadComponent from './UploadComponent';
import QuickUpload from './QuickUpload';

// Firebase configuration (will be loaded from env)
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || 'AIzaSyBa3-3tQDUZV26d51Ov-B2IYpW4FzRBoh8',
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || 'datadc-portal-auth.firebaseapp.com',
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || 'datadc-portal-auth',
  appId: process.env.REACT_APP_FIREBASE_APP_ID || '1:558894129249:web:7b518fe8b8adfc61248a20',
};

// Initialize Firebase with error handling
try {
  if (!firebase.apps.length) {
    firebase.initializeApp(firebaseConfig);
    console.log('Firebase initialized successfully');
  }
} catch (error) {
  console.error('Firebase initialization error:', error);
}

const auth = firebase.auth();

// API base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://provider-portal-api-production.gustavdatadc.workers.dev';

// Auth Context
const AuthContext = createContext(null);

const useAuth = () => useContext(AuthContext);

// ==========================================
// AUTH PROVIDER
// ==========================================

const AuthProvider = ({ children }) => {
  const [user, loading, error] = useAuthState(auth);
  const [apiToken, setApiToken] = useState(null);
  const [tenantId, setTenantId] = useState(null);
  const [mfaVerified, setMfaVerified] = useState(false);

  // Get Firebase token when user logs in
  useEffect(() => {
    if (user) {
      user.getIdToken(true).then((token) => {
        setApiToken(token);
        // Extract tenant_id from custom claims
        user.getIdTokenResult().then((idTokenResult) => {
          setTenantId(idTokenResult.claims.tenant_id);
          setMfaVerified(idTokenResult.claims.mfa_verified || false);
        });
      });
    } else {
      setApiToken(null);
      setTenantId(null);
      setMfaVerified(false);
    }
  }, [user]);

  // Session timeout (30 min idle)
  useEffect(() => {
    let timeout;
    const resetTimer = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        if (auth.currentUser) {
          auth.signOut();
          alert('Session timed out due to inactivity. Please log in again.');
        }
      }, 30 * 60 * 1000); // 30 minutes
    };

    window.addEventListener('mousemove', resetTimer);
    window.addEventListener('keypress', resetTimer);
    resetTimer();

    return () => {
      clearTimeout(timeout);
      window.removeEventListener('mousemove', resetTimer);
      window.removeEventListener('keypress', resetTimer);
    };
  }, []);

  const value = {
    user,
    apiToken,
    tenantId,
    mfaVerified,
    loading,
    error,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// ==========================================
// LOGIN COMPONENT
// ==========================================

const Login = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [verificationId, setVerificationId] = useState(null);
  const [isSignUp, setIsSignUp] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [practiceName, setPracticeName] = useState('');
  const [signUpSuccess, setSignUpSuccess] = useState(false);

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate('/', { replace: true });
    }
  }, [user, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Trim whitespace from email to prevent auth errors
    const trimmedEmail = email.trim();

    try {
      await auth.signInWithEmailAndPassword(trimmedEmail, password);
      // Success - will redirect via useEffect above
    } catch (err) {
      // Handle MFA requirement
      if (err.code === 'auth/multi-factor-auth-required') {
        setVerificationId(err.verificationId);
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const trimmedEmail = email.trim();

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      setLoading(false);
      return;
    }

    try {
      // Create user in Firebase
      const userCredential = await auth.createUserWithEmailAndPassword(trimmedEmail, password);
      
      // Update profile
      await userCredential.user.updateProfile({
        displayName: practiceName || trimmedEmail.split('@')[0]
      });

      // Call API to set up tenant
      const idToken = await userCredential.user.getIdToken();
      const response = await fetch(`${API_BASE_URL}/api/auth/setup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          email: trimmedEmail,
          practiceName: practiceName || trimmedEmail.split('@')[0],
          tenantId: trimmedEmail.split('@')[1]?.replace(/\./g, '-') || 'new-provider'
        })
      });

      if (!response.ok) {
        console.warn('Tenant setup API call failed, continuing with login');
      }

      setSignUpSuccess(true);
      // Auto-login after delay
      setTimeout(() => {
        navigate('/', { replace: true });
      }, 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMFA = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const credential = firebase.auth.PhoneAuthProvider.credential(
        verificationId,
        mfaCode
      );
      await auth.currentUser.multiFactor.enroll(credential, 'Phone');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (verificationId) {
    return (
      <div className="auth-container">
        <div className="auth-box">
          <h2>Two-Factor Authentication</h2>
          <p>Enter the code sent to your phone:</p>
          <form onSubmit={handleMFA}>
            <input
              type="text"
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value)}
              placeholder="6-digit code"
              maxLength="6"
              required
            />
            {error && <div className="error">{error}</div>}
            <button type="submit" disabled={loading}>
              {loading ? 'Verifying...' : 'Verify'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (signUpSuccess) {
    return (
      <div className="auth-container">
        <div className="auth-box">
          <div className="logo">🏥 DataDriven Commonsense</div>
          <h1>✅ Account Created!</h1>
          <p className="subtitle">Redirecting to dashboard...</p>
          <div className="success-message" style={{marginTop: '20px', color: '#00C853'}}>
            Your account has been created successfully.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-box">
        <div className="logo">
          🏥 DataDriven Commonsense
        </div>
        <h1>{isSignUp ? 'Create Account' : 'Provider Portal'}</h1>
        <p className="subtitle">{isSignUp ? 'Sign up for secure access' : 'HIPAA-compliant secure access'}</p>

        <form onSubmit={isSignUp ? handleSignUp : handleLogin}>
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@practice.com"
              required
              autoComplete="email"
            />
          </div>

          <div className="form-group">
            <label>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              autoComplete={isSignUp ? "new-password" : "current-password"}
            />
          </div>

          {isSignUp && (
            <>
              <div className="form-group">
                <label>Confirm Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  autoComplete="new-password"
                />
              </div>
              <div className="form-group">
                <label>Practice/Organization Name (Optional)</label>
                <input
                  type="text"
                  value={practiceName}
                  onChange={(e) => setPracticeName(e.target.value)}
                  placeholder="Your Medical Practice"
                  autoComplete="organization"
                />
              </div>
            </>
          )}

          {error && (
            <div className="error">
              ⚠️ {error}
            </div>
          )}

          <button type="submit" disabled={loading || !email || !password || (isSignUp && !confirmPassword)}>
            {loading ? (isSignUp ? 'Creating account...' : 'Signing in...') : (isSignUp ? 'Create Account' : 'Sign In')}
          </button>
        </form>

        <div className="auth-toggle" style={{marginTop: '20px', textAlign: 'center'}}>
          {isSignUp ? (
            <>
              Already have an account?{' '}
              <button 
                type="button" 
                onClick={() => {setIsSignUp(false); setError(null);}}
                style={{background: 'none', border: 'none', color: '#0057B8', cursor: 'pointer', textDecoration: 'underline'}}
              >
                Sign In
              </button>
            </>
          ) : (
            <>
              Don't have an account?{' '}
              <button 
                type="button" 
                onClick={() => {setIsSignUp(true); setError(null);}}
                style={{background: 'none', border: 'none', color: '#0057B8', cursor: 'pointer', textDecoration: 'underline'}}
              >
                Sign Up
              </button>
            </>
          )}
        </div>

        {!isSignUp && (
          <div className="help-links">
            <a href="mailto:support@datadc-pr.com">Forgot password?</a>
            <span> • </span>
            <a href="mailto:support@datadc-pr.com">Need help?</a>
          </div>
        )}
      </div>
    </div>
  );
};

// ==========================================
// PERSONALIZED HOME DASHBOARD COMPONENT
// ==========================================

const Dashboard = () => {
  const { user, apiToken, tenantId } = useAuth();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('home');
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    paid: 0,
    denied: 0,
    revenue: 0,
    avgProcessingTime: 0,
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [debugInfo, setDebugInfo] = useState(null);
  const [selectedCase, setSelectedCase] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedCase, setEditedCase] = useState(null);
  const [isReprocessing, setIsReprocessing] = useState(false);
  const [isProcessingCases, setIsProcessingCases] = useState(false);
  const [processingResult, setProcessingResult] = useState(null);
  const [processingCaseId, setProcessingCaseId] = useState(null);

  useEffect(() => {
    if (apiToken) {
      fetchCases();
      fetchActivity();
    }
  }, [apiToken]);

  const fetchCases = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/cases`, {
        headers: {
          'Authorization': `Bearer ${apiToken}`,
        },
      });

      if (!response.ok) throw new Error('Failed to fetch cases');

      const data = await response.json();
      setCases(data.cases || []);
      
      // Store debug info
      if (data._debug) {
        console.log('API Debug:', data._debug);
        setDebugInfo(data._debug);
      }

      // Calculate provider-specific metrics
      const metrics = data.metrics || {};
      const now = new Date();
      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();
      
      // Monthly Revenue: cases created this month
      const thisMonthCases = data.cases.filter(c => {
        const created = new Date(c.created_at);
        return created.getMonth() === currentMonth && created.getFullYear() === currentYear;
      });
      const monthlyRevenue = thisMonthCases.reduce((sum, c) => sum + parseFloat(c.charge_amount || 0), 0);
      
      // Collection Rate: paid / total
      const paidCases = data.cases.filter(c => c.status === 'APPROVED' || c.status === 'PAID');
      const collectionRate = data.cases.length > 0 ? Math.round((paidCases.length / data.cases.length) * 100) : 0;
      
      // Clean Claim Rate from API or calculate
      const cleanClaimRate = metrics.clean_claim_rate || (data.cases.length > 0 
        ? Math.round(((data.cases.length - (metrics.denied_cases || 0)) / data.cases.length) * 100) 
        : 0);
      
      const stats = {
        total: metrics.total_cases || data.cases.length,
        pending: metrics.pending_cases || data.cases.filter(c => ['NEW', 'PENDING', 'SUBMITTED', 'IN_REVIEW', 'CODED', 'BILLED'].includes(c.status)).length,
        paid: paidCases.length,
        denied: metrics.denied_cases || data.cases.filter(c => ['DENIED', 'APPEAL'].includes(c.status)).length,
        revenue: monthlyRevenue, // This month's revenue only
        totalRevenue: data.cases.reduce((sum, c) => sum + parseFloat(c.charge_amount || 0), 0), // All time
        avgProcessingTime: calculateAvgProcessingTime(data.cases),
        collectionRate: collectionRate,
        cleanClaimRate: cleanClaimRate,
        thisMonthCount: thisMonthCases.length,
      };
      setStats(stats);

      // Generate alerts
      generateAlerts(data.cases);
    } catch (err) {
      console.error('Error fetching cases:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchActivity = async () => {
    // Fetch from API when available, for now show empty
    setRecentActivity([]);
  };

  const calculateAvgProcessingTime = (cases) => {
    const completedCases = cases.filter(c => (c.status === 'PAID' || c.status === 'APPROVED') && c.created_at);
    if (completedCases.length === 0) return null; // No completed cases to calculate
    
    // Calculate actual days from creation to now (or use completed date if available)
    const totalDays = completedCases.reduce((sum, c) => {
      const created = new Date(c.created_at);
      const completed = c.completed_at ? new Date(c.completed_at) : new Date();
      const days = Math.floor((completed - created) / (1000 * 60 * 60 * 24));
      return sum + days;
    }, 0);
    
    return Math.round(totalDays / completedCases.length);
  };

  const generateAlerts = (cases) => {
    const newAlerts = [];
    
    // Check for denied cases needing attention
    const deniedCases = cases.filter(c => c.status === 'DENIED');
    if (deniedCases.length > 0) {
      newAlerts.push({
        type: 'urgent',
        message: `${deniedCases.length} denied case(s) require appeal action`,
        action: 'Review',
      });
    }

    // Check for old pending cases
    const oldPending = cases.filter(c => 
      ['NEW', 'IN_REVIEW'].includes(c.status) && c.created_at
    );
    if (oldPending.length > 5) {
      newAlerts.push({
        type: 'warning',
        message: `${oldPending.length} cases pending review for >7 days`,
        action: 'View',
      });
    }

    // Check for high-value cases
    const highValueCases = cases.filter(c => parseFloat(c.charge_amount) > 5000);
    if (highValueCases.length > 0) {
      newAlerts.push({
        type: 'info',
        message: `${highValueCases.length} high-value cases ($5K+) this month`,
        action: 'Details',
      });
    }

    setAlerts(newAlerts);
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const getPracticeName = () => {
    // In production, fetch from tenant profile
    return user?.email?.split('@')[1]?.split('.')[0]?.toUpperCase() || 'Your Practice';
  };

  const handleLogout = async () => {
    await auth.signOut();
  };

  const handleEditCase = () => {
    setIsEditing(true);
    setEditedCase({ ...selectedCase });
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedCase(null);
  };

  const handleSaveEdit = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/cases/${editedCase.case_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiToken}`
        },
        body: JSON.stringify(editedCase)
      });

      if (!response.ok) {
        throw new Error('Failed to save changes');
      }

      setIsEditing(false);
      alert('Changes saved. If data was modified, you should reprocess the case.');
      fetchCases();
    } catch (err) {
      alert('Error saving changes: ' + err.message);
    }
  };

  const handleReprocessCase = async () => {
    if (!window.confirm('Reprocess this case? A NEW case ID will be created and this case will be marked as reprocessed.')) {
      return;
    }

    setIsReprocessing(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/cases/${selectedCase.case_id}/reprocess`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiToken}`
        },
        body: JSON.stringify({
          reason: 'Client data correction',
          originalCaseId: selectedCase.case_id
        })
      });

      if (!response.ok) {
        throw new Error('Reprocessing failed');
      }

      const data = await response.json();
      
      alert(`✅ Case reprocessed!\n\nOriginal: ${selectedCase.case_id}\nNew: ${data.newCaseId}`);
      
      setSelectedCase(null);
      fetchCases();
    } catch (err) {
      alert('Error reprocessing: ' + err.message);
    } finally {
      setIsReprocessing(false);
    }
  };

  const handleProcessSingleCase = async (caseId) => {
    if (!window.confirm(`Process case ${caseId} with AI? This will extract patient data, CPT/ICD codes, and payer information.`)) {
      return;
    }

    setProcessingCaseId(caseId);

    try {
      const response = await fetch(`${API_BASE_URL}/api/cases/${caseId}/process`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${apiToken}`,
          'Content-Type': 'application/json'
        }
      });

      // Check status before parsing (might be HTML error page)
      const contentType = response.headers.get('content-type');
      const isJson = contentType && contentType.includes('application/json');
      
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        // Response wasn't JSON - likely a server error
        const text = await response.text();
        console.error('[PROCESS] Non-JSON response:', text.substring(0, 500));
        throw new Error(`Server error (${response.status}): Not a valid JSON response`);
      }

      if (!response.ok) {
        // Log full error details for debugging
        console.error('[PROCESS] API Error:', {
          status: response.status,
          error: data.error,
          details: data.details,
          stack: data.stack,
          step: data.step
        });
        throw new Error(data.error || data.details || `Processing failed (${response.status})`);
      }

      if (data.queued) {
        // Case was queued, show message and start polling
        alert(`✅ Case ${caseId} queued for AI processing!\n\nJob ID: ${data.jobId}\n\nProcessing happens in the background. Refresh in 1-2 minutes to see results.`);
        
        // Start polling for status
        if (data.jobId) {
          pollJobStatus(data.jobId, caseId);
        }
      } else if (data.success) {
        alert(`✅ Case ${caseId} processed!\n\nStatus: ${data.status}\n\nRefresh to see updated data.`);
      }
      
      fetchCases(); // Refresh to show updated status
    } catch (err) {
      alert(`Error processing case ${caseId}: ${err.message}`);
    } finally {
      setProcessingCaseId(null);
    }
  };

  const pollJobStatus = async (jobId, caseId) => {
    const maxAttempts = 30; // Poll for 5 minutes (10s intervals)
    let attempts = 0;
    
    const checkStatus = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/jobs/${jobId}/status`, {
          headers: { 'Authorization': `Bearer ${apiToken}` }
        });
        
        if (!response.ok) return;
        
        const data = await response.json();
        
        if (data.status === 'completed') {
          console.log(`Job ${jobId} completed, refreshing cases...`);
          fetchCases();
          return;
        }
        
        if (data.status === 'failed') {
          console.log(`Job ${jobId} failed:`, data.errorMessage);
          return;
        }
        
        // Continue polling
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(checkStatus, 10000); // Check every 10 seconds
        }
      } catch (err) {
        console.error('Poll error:', err);
      }
    };
    
    // Start polling after 5 seconds
    setTimeout(checkStatus, 5000);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading your personalized dashboard...</p>
      </div>
    );
  }

  return (
    <div className="portal-dashboard">
      <header className="portal-header">
        <div className="header-left">
          <div className="logo">🏥</div>
          <div>
            <h1>Provider Portal</h1>
            <p>{getPracticeName()}</p>
          </div>
        </div>
        <div className="header-right">
          <span className="badge">🔒 Secure</span>
          <button onClick={handleLogout} className="logout-btn">Sign Out</button>
        </div>
      </header>

      {/* Navigation Tabs */}
      <nav className="dashboard-nav">
        <button 
          className={`nav-tab ${activeTab === 'home' ? 'active' : ''}`}
          onClick={() => setActiveTab('home')}
        >
          🏠 Home
        </button>
        <button 
          className={`nav-tab ${activeTab === 'cases' ? 'active' : ''}`}
          onClick={() => setActiveTab('cases')}
        >
          📋 Cases ({stats.total})
        </button>
        <button 
          className={`nav-tab ${activeTab === 'upload' ? 'active' : ''}`}
          onClick={() => setActiveTab('upload')}
        >
          📤 Quick Upload
        </button>
        <button 
          className={`nav-tab ${activeTab === 'reports' ? 'active' : ''}`}
          onClick={() => setActiveTab('reports')}
        >
          📊 Reports
        </button>
        <button 
          className={`nav-tab ${activeTab === 'glossary' ? 'active' : ''}`}
          onClick={() => setActiveTab('glossary')}
        >
          📖 Glossary
        </button>
      </nav>

      <div className="dashboard-content">
        {/* HOME TAB - Personalized Dashboard */}
        {activeTab === 'home' && (
          <>
            {/* Welcome Section */}
            <div className="welcome-section">
              <h2>{getGreeting()}, {user?.email?.split('@')[0]}</h2>
              <p className="welcome-subtitle">
                Here's what's happening at {getPracticeName()} today
              </p>
            </div>

            {/* Quick Stats Row */}
            <div className="stats-row home-stats">
              <div className="stat-card highlight">
                <div className="stat-icon">💰</div>
                <div className="stat-value">${stats.revenue.toLocaleString()}</div>
                <div className="stat-label">Revenue This Month</div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">⏱️</div>
                <div className="stat-value">{stats.avgProcessingTime} days</div>
                <div className="stat-label">Avg. Processing Time</div>
              </div>
              <div className="stat-card success">
                <div className="stat-icon">✅</div>
                <div className="stat-value">{stats.paid}</div>
                <div className="stat-label">Cases Paid</div>
              </div>
              <div className="stat-card warning">
                <div className="stat-icon">⏳</div>
                <div className="stat-value">{stats.pending}</div>
                <div className="stat-label">Pending</div>
              </div>
            </div>

            {/* Alerts Section */}
            {alerts.length > 0 && (
              <div className="alerts-section">
                <h3>🚨 Action Required</h3>
                {alerts.map((alert, idx) => (
                  <div key={idx} className={`alert-card ${alert.type}`}>
                    <span className="alert-message">{alert.message}</span>
                    <button className="alert-action">{alert.action}</button>
                  </div>
                ))}
              </div>
            )}

            {/* Quick Actions */}
            <div className="quick-actions">
              <h3>Quick Actions</h3>
              <div className="action-grid">
                <Link to="/new-case" className="action-card primary">
                  <div className="action-icon">➕</div>
                  <div className="action-title">Submit New Case</div>
                  <div className="action-desc">Create a new billing case</div>
                </Link>
                <button className="action-card" onClick={() => setActiveTab('upload')}>
                  <div className="action-icon">📎</div>
                  <div className="action-title">Upload Files</div>
                  <div className="action-desc">Medical records, documents</div>
                </button>
                <button className="action-card" onClick={() => setActiveTab('cases')}>
                  <div className="action-icon">🔍</div>
                  <div className="action-title">View All Cases</div>
                  <div className="action-desc">Check status & updates</div>
                </button>
                <button className="action-card" onClick={() => setActiveTab('reports')}>
                  <div className="action-icon">📈</div>
                  <div className="action-title">View Reports</div>
                  <div className="action-desc">Analytics & insights</div>
                </button>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="activity-section">
              <h3>Recent Activity</h3>
              <div className="activity-list">
                {recentActivity.map((activity, idx) => (
                  <div key={idx} className={`activity-item ${activity.type}`}>
                    <div className="activity-icon">
                      {activity.type === 'case_created' && '📝'}
                      {activity.type === 'payment_received' && '💵'}
                      {activity.type === 'status_update' && '🔄'}
                      {activity.type === 'file_uploaded' && '📎'}
                    </div>
                    <div className="activity-content">
                      <div className="activity-message">{activity.message}</div>
                      <div className="activity-time">{activity.time}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Performance Summary - Only show if we have data */}
            {stats.total > 0 && (
            <div className="performance-section">
              <h3>Performance Summary</h3>
              <div className="performance-grid">
                <div className="performance-item">
                  <div className="performance-label">Clean Claim Rate</div>
                  <div className="performance-value">{stats.cleanClaimRate || 0}%</div>
                  <div className="performance-bar">
                    <div className="performance-fill" style={{width: `${stats.cleanClaimRate || 0}%`}}></div>
                  </div>
                </div>
                <div className="performance-item">
                  <div className="performance-label">Collection Rate</div>
                  <div className="performance-value">{stats.collectionRate || 0}%</div>
                  <div className="performance-bar">
                    <div className="performance-fill" style={{width: `${stats.collectionRate || 0}%`}}></div>
                  </div>
                </div>
                <div className="performance-item">
                  <div className="performance-label">Avg. Days in AR</div>
                  <div className="performance-value">{stats.avgProcessingTime || 'N/A'} {stats.avgProcessingTime ? 'days' : ''}</div>
                  <div className="performance-bar">
                    <div className="performance-fill warning" style={{width: '60%'}}></div>
                  </div>
                </div>
              </div>
            </div>
            )}
          </>
        )}

        {/* CASES TAB */}
        {activeTab === 'cases' && (
          <>
            <div className="tab-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
              <h2>All Cases</h2>
              <button className="btn-secondary" onClick={fetchCases}>
                🔄 Refresh
              </button>
            </div>
            <div className="stats-row">
              <div className="stat-card">
                <div className="stat-value">{stats.total}</div>
                <div className="stat-label">Total Cases</div>
              </div>
              <div className="stat-card pending">
                <div className="stat-value">{stats.pending}</div>
                <div className="stat-label">Pending</div>
              </div>
              <div className="stat-card paid">
                <div className="stat-value">{stats.paid}</div>
                <div className="stat-label">Paid</div>
              </div>
              <div className="stat-card denied">
                <div className="stat-value">{stats.denied}</div>
                <div className="stat-label">Denied</div>
              </div>
            </div>
            
            {debugInfo && (
              <div className="debug-panel" style={{background: '#f0f0f0', padding: '10px', margin: '10px 0', fontSize: '12px', fontFamily: 'monospace'}}>
                <strong>Debug Info:</strong><br/>
                Tenant: {debugInfo.tenant_id}<br/>
                Email: {debugInfo.user_email}<br/>
                Data Source: {debugInfo.data_source}<br/>
                Cases: {debugInfo.case_ids?.join(', ')}
              </div>
            )}

            <div className="cases-table-container">
              <table className="cases-table">
                <thead>
                  <tr>
                    <th>Case ID</th>
                    <th>Patient</th>
                    <th>Procedure</th>
                    <th>Status</th>
                    <th>Priority</th>
                    <th>Amount</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {cases.map((c) => (
                    <tr key={c.case_id}>
                      <td className="case-id">
                        <button 
                          className="case-link"
                          onClick={() => setSelectedCase(c)}
                          style={{ background: 'none', border: 'none', color: '#0057B8', textDecoration: 'underline', cursor: 'pointer', padding: 0, font: 'inherit' }}
                        >
                          {c.case_id}
                        </button>
                      </td>
                      <td>{c.patient_name}</td>
                      <td>{c.procedure_name}</td>
                      <td>
                        <span className={`status-badge ${c.status.toLowerCase()}`}>
                          {c.status}
                        </span>
                        {(c.status === 'NEW' || c.status === 'QUEUED_FOR_AI' || c.status === 'PARSING_FAILED') && (
                          <button
                            onClick={() => handleProcessSingleCase(c.case_id)}
                            disabled={processingCaseId === c.case_id}
                            style={{
                              marginLeft: '8px',
                              padding: '4px 10px',
                              fontSize: '12px',
                              background: processingCaseId === c.case_id ? '#ccc' : '#667eea',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: processingCaseId === c.case_id ? 'not-allowed' : 'pointer',
                            }}
                          >
                            {processingCaseId === c.case_id ? '⏳' : c.status === 'QUEUED_FOR_AI' ? '🔄 Queue' : '🤖 Process'}
                          </button>
                        )}
                      </td>
                      <td>
                        <span className={`priority-badge ${c.priority}`}>
                          {c.priority}
                        </span>
                      </td>
                      <td className="amount">${parseFloat(c.charge_amount).toLocaleString()}</td>
                      <td>{new Date(c.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                  {cases.length === 0 && (
                    <tr>
                      <td colSpan="7" className="empty-state">
                        No cases found. <Link to="/new-case">Submit your first case</Link>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* UPLOAD TAB - QUICK UPLOAD (Fire and Forget) */}
        {activeTab === 'upload' && (
          <QuickUpload apiToken={apiToken} onUploadComplete={fetchCases} />
        )}

        {/* REPORTS TAB */}
        {activeTab === 'reports' && (
          <>
            <div className="tab-header">
              <h2>📊 Reports & Analytics</h2>
              <p className="tab-subtitle">Track your practice's billing performance</p>
            </div>
            
            <div className="reports-grid">
              <div className="report-card">
                <h3>📈 Monthly Revenue ({stats.thisMonthCount || 0} cases)</h3>
                <div className="report-value">${stats.revenue?.toLocaleString() || 0}</div>
                <div className="report-trend neutral">This month only</div>
              </div>
              
              <div className="report-card">
                <h3>⏱️ Avg. Processing Time</h3>
                <div className="report-value">{stats.avgProcessingTime || 'N/A'} {stats.avgProcessingTime ? 'days' : ''}</div>
                <div className="report-trend neutral">{stats.paid > 0 ? 'Based on paid cases' : 'No completed cases'}</div>
              </div>
              
              <div className="report-card">
                <h3>✅ Collection Rate</h3>
                <div className="report-value">{stats.collectionRate || 0}%</div>
                <div className="report-trend neutral">{stats.paid || 0} of {stats.total || 0} cases paid</div>
              </div>
              
              <div className="report-card">
                <h3>🎯 Clean Claim Rate</h3>
                <div className="report-value">{stats.cleanClaimRate || 0}%</div>
                <div className="report-trend neutral">{stats.denied || 0} denied of {stats.total || 0} total</div>
              </div>
            </div>
            
            <div className="reports-actions">
              <button className="btn-primary">📥 Download Full Report</button>
              <button className="btn-secondary">📅 Schedule Monthly Reports</button>
            </div>
          </>
        )}
        
        {/* GLOSSARY TAB - Metrics Definitions */}
        {activeTab === 'glossary' && (
          <>
            <div className="tab-header">
              <h2>📖 Metrics Glossary</h2>
              <p className="tab-subtitle">Understanding your dashboard metrics</p>
            </div>
            
            <div className="glossary-content">
              <div className="glossary-section">
                <h3>💰 Revenue Metrics</h3>
                <div className="glossary-item">
                  <h4>Revenue This Month</h4>
                  <p>Total dollar amount of all cases submitted or processed during the current calendar month. Includes pending, approved, and paid cases.</p>
                </div>
                <div className="glossary-item">
                  <h4>Total Case Value</h4>
                  <p>Sum of all charge amounts across your case portfolio, regardless of payment status.</p>
                </div>
              </div>
              
              <div className="glossary-section">
                <h3>📊 Case Status Metrics</h3>
                <div className="glossary-item">
                  <h4>Total Cases</h4>
                  <p>Total number of cases in your account across all statuses.</p>
                </div>
                <div className="glossary-item">
                  <h4>Pending</h4>
                  <p>Cases awaiting action: NEW (just created), SUBMITTED (sent to payer), IN_REVIEW (under review), CODED (needs coding), BILLED (billing in progress).</p>
                </div>
                <div className="glossary-item">
                  <h4>Cases Paid</h4>
                  <p>Cases with payment received or approved status. Indicates successful reimbursement.</p>
                </div>
                <div className="glossary-item">
                  <h4>Denied</h4>
                  <p>Cases rejected by payer or in appeal status. Requires corrective action.</p>
                </div>
              </div>
              
              <div className="glossary-section">
                <h3>⏱️ Performance Metrics</h3>
                <div className="glossary-item">
                  <h4>Avg. Processing Time</h4>
                  <p>Average number of days from case creation to payment receipt. Lower is better. Industry benchmark is 30-45 days.</p>
                </div>
                <div className="glossary-item">
                  <h4>Clean Claim Rate</h4>
                  <p>Percentage of claims accepted by payers on first submission without errors or rejections. Target: >95%.</p>
                </div>
                <div className="glossary-item">
                  <h4>Collection Rate</h4>
                  <p>Percentage of total billed amount actually collected. Calculated as (Paid Cases / Total Cases) × 100.</p>
                </div>
              </div>
              
              <div className="glossary-section">
                <h3>🏥 Case Status Definitions</h3>
                <div className="glossary-item">
                  <h4>NEW</h4>
                  <p>Case created but not yet submitted to payer.</p>
                </div>
                <div className="glossary-item">
                  <h4>SUBMITTED</h4>
                  <p>Case sent to insurance payer for review.</p>
                </div>
                <div className="glossary-item">
                  <h4>IN_REVIEW</h4>
                  <p>Payer is reviewing the claim (medical review, prior auth pending).</p>
                </div>
                <div className="glossary-item">
                  <h4>APPROVED</h4>
                  <p>Claim approved by payer, payment pending.</p>
                </div>
                <div className="glossary-item">
                  <h4>PAID</h4>
                  <p>Payment received and posted.</p>
                </div>
                <div className="glossary-item">
                  <h4>DENIED</h4>
                  <p>Claim rejected - requires correction or appeal.</p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      
      {/* CASE DETAIL MODAL */}
      {selectedCase && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '8px',
            maxWidth: '800px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
          }}>
            <div style={{ padding: '24px', borderBottom: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0 }}>📋 Case Details: {selectedCase.id}</h2>
              <button 
                onClick={() => setSelectedCase(null)}
                style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}
              >
                ×
              </button>
            </div>
            
            <div style={{ padding: '24px' }}>
              {/* Status & Priority */}
              <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: '200px' }}>
                  <label style={{ fontSize: '12px', color: '#666', textTransform: 'uppercase' }}>Status</label>
                  <div style={{ 
                    display: 'inline-block',
                    padding: '6px 12px', 
                    borderRadius: '4px', 
                    backgroundColor: selectedCase.status === 'PAID' ? '#d4edda' : 
                                     selectedCase.status === 'DENIED' ? '#f8d7da' :
                                     selectedCase.status === 'NEW' ? '#fff3cd' : '#e3f2fd',
                    color: selectedCase.status === 'PAID' ? '#155724' : 
                           selectedCase.status === 'DENIED' ? '#721c24' :
                           selectedCase.status === 'NEW' ? '#856404' : '#0c5460',
                    fontWeight: 'bold',
                    marginTop: '4px'
                  }}>
                    {selectedCase.status}
                  </div>
                </div>
                <div style={{ flex: 1, minWidth: '200px' }}>
                  <label style={{ fontSize: '12px', color: '#666', textTransform: 'uppercase' }}>Priority</label>
                  <div style={{ fontWeight: 'bold', marginTop: '4px' }}>{selectedCase.priority}</div>
                </div>
                <div style={{ flex: 1, minWidth: '200px' }}>
                  <label style={{ fontSize: '12px', color: '#666', textTransform: 'uppercase' }}>Amount</label>
                  <div style={{ fontWeight: 'bold', fontSize: '18px', marginTop: '4px' }}>
                    ${parseFloat(selectedCase.charge_amount).toLocaleString()}
                  </div>
                </div>
              </div>
              
              {/* Extracted Data Sections */}
              {selectedCase.patient && (
                <div style={{ marginBottom: '20px' }}>
                  <h3 style={{ borderBottom: '2px solid #0057B8', paddingBottom: '8px', color: '#0057B8' }}>👤 Patient Information</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
                    <div>
                      <label style={{ fontSize: '12px', color: '#666' }}>Name</label>
                      <div>{selectedCase.patient.name || 'N/A'}</div>
                    </div>
                    <div>
                      <label style={{ fontSize: '12px', color: '#666' }}>MRN</label>
                      <div>{selectedCase.patient.mrn || 'N/A'}</div>
                    </div>
                    <div>
                      <label style={{ fontSize: '12px', color: '#666' }}>Date of Birth</label>
                      <div>{selectedCase.patient.dob || 'N/A'}</div>
                    </div>
                    <div>
                      <label style={{ fontSize: '12px', color: '#666' }}>Gender</label>
                      <div>{selectedCase.patient.gender || 'N/A'}</div>
                    </div>
                  </div>
                </div>
              )}
              
              {selectedCase.service && (
                <div style={{ marginBottom: '20px' }}>
                  <h3 style={{ borderBottom: '2px solid #0057B8', paddingBottom: '8px', color: '#0057B8' }}>🏥 Service Information</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
                    <div>
                      <label style={{ fontSize: '12px', color: '#666' }}>Date of Service</label>
                      <div>{selectedCase.service.date || 'N/A'}</div>
                    </div>
                    <div>
                      <label style={{ fontSize: '12px', color: '#666' }}>Provider</label>
                      <div>{selectedCase.service.provider || 'N/A'}</div>
                    </div>
                    <div>
                      <label style={{ fontSize: '12px', color: '#666' }}>Facility</label>
                      <div>{selectedCase.service.facility || 'N/A'}</div>
                    </div>
                    <div>
                      <label style={{ fontSize: '12px', color: '#666' }}>Procedure</label>
                      <div>{selectedCase.service.procedure_description || selectedCase.procedure_name || 'N/A'}</div>
                    </div>
                  </div>
                </div>
              )}
              
              {selectedCase.coding && (
                <div style={{ marginBottom: '20px' }}>
                  <h3 style={{ borderBottom: '2px solid #0057B8', paddingBottom: '8px', color: '#0057B8' }}>💊 Coding</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
                    <div>
                      <label style={{ fontSize: '12px', color: '#666' }}>Primary CPT</label>
                      <div style={{ fontFamily: 'monospace', fontSize: '14px' }}>{selectedCase.coding.cpt_primary || 'N/A'}</div>
                    </div>
                    <div>
                      <label style={{ fontSize: '12px', color: '#666' }}>Primary ICD-10</label>
                      <div style={{ fontFamily: 'monospace', fontSize: '14px' }}>{selectedCase.coding.icd10_primary || 'N/A'}</div>
                    </div>
                    {selectedCase.coding.cpt_secondary?.length > 0 && (
                      <div>
                        <label style={{ fontSize: '12px', color: '#666' }}>Secondary CPTs</label>
                        <div style={{ fontFamily: 'monospace' }}>{selectedCase.coding.cpt_secondary.join(', ')}</div>
                      </div>
                    )}
                    {selectedCase.coding.icd10_secondary?.length > 0 && (
                      <div>
                        <label style={{ fontSize: '12px', color: '#666' }}>Secondary ICD-10s</label>
                        <div style={{ fontFamily: 'monospace' }}>{selectedCase.coding.icd10_secondary.join(', ')}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {selectedCase.payer && (
                <div style={{ marginBottom: '20px' }}>
                  <h3 style={{ borderBottom: '2px solid #0057B8', paddingBottom: '8px', color: '#0057B8' }}>🏛️ Payer Information</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
                    <div>
                      <label style={{ fontSize: '12px', color: '#666' }}>Type</label>
                      <div>{selectedCase.payer.type || 'N/A'}</div>
                    </div>
                    <div>
                      <label style={{ fontSize: '12px', color: '#666' }}>Name</label>
                      <div>{selectedCase.payer.name || 'N/A'}</div>
                    </div>
                    <div>
                      <label style={{ fontSize: '12px', color: '#666' }}>Policy #</label>
                      <div>{selectedCase.payer.policy_number || 'N/A'}</div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Raw Extracted Text */}
              {selectedCase.notes && selectedCase.notes.includes('RAW_EXTRACTED_TEXT') && (
                <div style={{ marginBottom: '20px' }}>
                  <h3 style={{ borderBottom: '2px solid #666', paddingBottom: '8px', color: '#666' }}>📝 Raw Extracted Text</h3>
                  <div style={{ 
                    backgroundColor: '#f5f5f5', 
                    padding: '12px', 
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontFamily: 'monospace',
                    whiteSpace: 'pre-wrap',
                    maxHeight: '200px',
                    overflow: 'auto',
                    marginTop: '12px'
                  }}>
                    {selectedCase.notes.split('RAW_EXTRACTED_TEXT:\n')[1] || selectedCase.notes}
                  </div>
                </div>
              )}
              
              {/* Audit Info */}
              {selectedCase.audit && (
                <div style={{ marginBottom: '20px' }}>
                  <h3 style={{ borderBottom: '2px solid #666', paddingBottom: '8px', color: '#666' }}>📊 Audit Trail</h3>
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '12px' }}>
                    <div>Created: {new Date(selectedCase.created_at).toLocaleString()}</div>
                    {selectedCase.audit.created_by && <div>By: {selectedCase.audit.created_by}</div>}
                    {selectedCase.audit.source && <div>Source: {selectedCase.audit.source}</div>}
                    {selectedCase.coding?.confidence && (
                      <div>AI Confidence: {(selectedCase.coding.confidence * 100).toFixed(1)}%</div>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            <div style={{ padding: '16px 24px', borderTop: '1px solid #e0e0e0', display: 'flex', gap: '12px', justifyContent: 'space-between' }}>
              <div>
                {/* Edit Button */}
                <button 
                  onClick={handleEditCase}
                  disabled={isEditing || isReprocessing}
                  style={{ 
                    padding: '8px 16px', 
                    border: '1px solid #0057B8', 
                    borderRadius: '4px', 
                    background: 'white',
                    color: '#0057B8',
                    cursor: 'pointer',
                    marginRight: '8px'
                  }}
                >
                  ✏️ Edit Case
                </button>
                
                {/* Reprocess Button */}
                <button 
                  onClick={handleReprocessCase}
                  disabled={isEditing || isReprocessing}
                  style={{ 
                    padding: '8px 16px', 
                    border: '1px solid #f57c00', 
                    borderRadius: '4px', 
                    background: 'white',
                    color: '#f57c00',
                    cursor: 'pointer'
                  }}
                >
                  {isReprocessing ? '⏳ Reprocessing...' : '🔄 Reprocess'}
                </button>
              </div>
              
              <div>
                <button 
                  onClick={() => setSelectedCase(null)}
                  style={{ 
                    padding: '8px 16px', 
                    border: '1px solid #ccc', 
                    borderRadius: '4px', 
                    background: 'white',
                    cursor: 'pointer'
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==========================================
// NEW CASE FORM
// ==========================================

const NewCase = () => {
  const { apiToken } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    patient_name: '',
    procedure: '',
    cpt_code: '',
    icd10_code: '',
    payer: '',
    charge: '',
    priority: 'P3',
    service_date: new Date().toISOString().split('T')[0],
    description: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/cases`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiToken}`,
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) throw new Error('Failed to create case');

      const data = await response.json();
      alert(`Case created successfully: ${data.case.id}`);
      navigate('/');
    } catch (err) {
      alert('Error creating case: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  return (
    <div className="portal-dashboard">
      <header className="portal-header">
        <Link to="/" className="back-link">← Back to Dashboard</Link>
        <h1>Submit New Case</h1>
      </header>

      <form className="case-form" onSubmit={handleSubmit}>
        <div className="form-section">
          <h3>Patient Information</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Patient Name *</label>
              <input
                type="text"
                name="patient_name"
                value={formData.patient_name}
                onChange={handleChange}
                placeholder="John Smith"
                required
              />
            </div>
            <div className="form-group">
              <label>Service Date *</label>
              <input
                type="date"
                name="service_date"
                value={formData.service_date}
                onChange={handleChange}
                required
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>Procedure & Coding</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Procedure *</label>
              <input
                type="text"
                name="procedure"
                value={formData.procedure}
                onChange={handleChange}
                placeholder="e.g., Annual Physical"
                required
              />
            </div>
            <div className="form-group">
              <label>CPT Code</label>
              <input
                type="text"
                name="cpt_code"
                value={formData.cpt_code}
                onChange={handleChange}
                placeholder="99213"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>ICD-10 Code</label>
              <input
                type="text"
                name="icd10_code"
                value={formData.icd10_code}
                onChange={handleChange}
                placeholder="Z00.00"
              />
            </div>
            <div className="form-group">
              <label>Charge Amount</label>
              <input
                type="number"
                name="charge"
                value={formData.charge}
                onChange={handleChange}
                placeholder="0.00"
                step="0.01"
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>Billing Information</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Insurance / Payer</label>
              <select name="payer" value={formData.payer} onChange={handleChange}>
                <option value="">Select Payer...</option>
                <option value="BCBS">Blue Cross Blue Shield</option>
                <option value="AETNA">Aetna</option>
                <option value="CIGNA">Cigna</option>
                <option value="MEDICARE">Medicare</option>
                <option value="UHC">United Healthcare</option>
                <option value="SELFPAY">Self-Pay</option>
              </select>
            </div>
            <div className="form-group">
              <label>Priority</label>
              <select name="priority" value={formData.priority} onChange={handleChange}>
                <option value="P3">P3 - Normal</option>
                <option value="P2">P2 - High</option>
                <option value="P1">P1 - Urgent</option>
              </select>
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>Clinical Notes</h3>
          <div className="form-group">
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="4"
              placeholder="Enter clinical description, symptoms, findings..."
            />
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn-primary" disabled={submitting}>
            {submitting ? 'Submitting...' : '✓ Submit Case'}
          </button>
          <Link to="/" className="btn-secondary">Cancel</Link>
        </div>
      </form>
    </div>
  );
};

// ==========================================
// PROTECTED ROUTE WRAPPER
// ==========================================

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// ==========================================
// MAIN APP
// ==========================================

const App = () => {
  return (
    <AuthProvider>
      <Router>
        <div className="app">
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/new-case"
              element={
                <ProtectedRoute>
                  <NewCase />
                </ProtectedRoute>
              }
            />
          </Routes>
        </div>
      </Router>
    </AuthProvider>
  );
};

export default App;
